import { betriebZuIntern } from "../../lib/bereich";
import { ApiError, type Api } from "../../data/api";
import { t } from "../../lib/sprache";

/**
 * Team der Betreiber-Konsole (Vertrieb-Konsole, Inhaber 02.11.2026, 0459): Mitarbeiter mit Rolle Admin oder Vertrieb,
 * Einladungen per E-Mail. Alles über Datenbankfunktionen; die Datenbank prüft Rolle und zweiten Faktor bei jedem Aufruf.
 */

export type StaffRole = "developer" | "admin" | "sales";

export interface StaffRow {
  user_id: string; email: string; display_name: string | null; role: StaffRole; active: boolean; mfa_required: boolean;
  factor_verified: boolean; created_at: string; deactivated_at: string | null; invited_by_email: string | null;
  last_login_at: string | null; me: boolean;
}
export interface StaffInvitationRow {
  id: string; email: string; role: StaffRole; display_name: string | null; created_at: string; expires_at: string;
  invited_by_email: string | null; status: "open" | "expired" | "accepted" | "revoked";
}
export interface TeamData { staff: StaffRow[]; invitations: StaffInvitationRow[] }

const parse = <T,>(r: unknown): T => (typeof r === "string" ? JSON.parse(r) : r) as T;

export const ROLLE: Record<StaffRole, string> = { developer: "Entwicklung", admin: "Admin", sales: "Vertrieb" };

/** Ein Satz je Rolle für Einladung und Rollenwahl (0500, Inhaber 03.11.2026) */
export function rollenErklaerung(r: StaffRole): string {
  switch (r) {
    case "developer": return t("Entwicklung darf alles, auch Systemeinstellungen, Rechtstexte, KI-Zugänge, Protokolle, Betrieb und das ganze Team.",
      "Development can do everything, including system settings, legal texts, AI access, logs, operations and the whole team.");
    case "admin": return t("Admin führt den Betrieb: Kunden, Tarife, Sperren, Testphasen, Rabattcodes, Support, Demo und das Einladen des Vertriebs.",
      "Admin runs operations: customers, plans, locks, trials, discount codes, support, demo and inviting sales.");
    default: return t("Vertrieb arbeitet mit Leads, Konten und Demo-Terminen und verlängert Testphasen um zusammen höchstens 14 Tage.",
      "Sales works with leads, accounts and demo appointments and extends trials by at most 14 days in total.");
  }
}

/** Rollen, die eine Person einladen bzw. vergeben darf: Entwicklung alle, Admin nur den Vertrieb (wie app.staff_may_manage) */
export function vergebbareRollen(eigene: "developer" | "admin" | "sales"): StaffRole[] {
  return eigene === "developer" ? ["sales", "admin", "developer"] : eigene === "admin" ? ["sales"] : [];
}

export async function loadTeam(api: Pick<Api, "read">): Promise<TeamData> {
  const r = parse<TeamData | null>(await api.read("platform_staff_list", {}));
  return { staff: r?.staff ?? [], invitations: r?.invitations ?? [] };
}

const EMAIL = /^[^@\s"<>]+@[^@\s"<>]+\.[^@\s"<>]{2,}$/;

/** Einladung anlegen; die Antwort enthält den Link (Pfad), damit der Admin ihn notfalls selbst weitergibt. */
export async function inviteStaff(api: Pick<Api, "rpc">, input: { email: string; role: StaffRole; name: string }): Promise<{ invitation_id: string; expires_at: string; link_path: string }> {
  const email = input.email.trim().toLowerCase();
  if (!EMAIL.test(email)) throw new ApiError("Bitte eine gültige E-Mail-Adresse angeben.");
  const name = input.name.trim();
  if (name && (name.length < 2 || name.length > 80)) throw new ApiError("Der Name hat 2 bis 80 Zeichen.");
  return parse(await api.rpc("platform_staff_invite", { p_email: email, p_role: input.role, p_name: name || null }));
}
export async function revokeStaffInvitation(api: Pick<Api, "rpc">, id: string): Promise<void> {
  await api.rpc("platform_staff_invitation_revoke", { p_invitation: id });
}
export async function setStaffRole(api: Pick<Api, "rpc">, userId: string, role: StaffRole): Promise<void> {
  await api.rpc("platform_staff_set_role", { p_user: userId, p_role: role });
}
export async function setStaffActive(api: Pick<Api, "rpc">, userId: string, active: boolean): Promise<void> {
  await api.rpc("platform_staff_set_active", { p_user: userId, p_active: active });
}
export async function setStaffName(api: Pick<Api, "rpc">, userId: string, name: string): Promise<void> {
  await api.rpc("platform_staff_set_name", { p_user: userId, p_name: name.trim() || null });
}

/** Annahmeseite: Stand der Einladung, ohne die eingeladene Adresse zu verraten */
export interface StaffInvitationInfo { status: "open" | "expired" | "accepted" | "revoked" | "unknown"; role?: StaffRole; expires_at?: string; email_matches?: boolean }
export async function staffInvitationInfo(api: Pick<Api, "read">, token: string): Promise<StaffInvitationInfo> {
  return parse<StaffInvitationInfo>(await api.read("platform_staff_invitation_info", { p_token: token }));
}
export async function acceptStaffInvitation(api: Pick<Api, "rpc">, token: string): Promise<{ role: StaffRole }> {
  return parse(await api.rpc("platform_staff_accept", { p_token: token }));
}

/** Volle Adresse des Einladungslinks aus dem Pfad der Datenbank (gleicher Ursprung wie die Konsole; ältere Datenbank: /betrieb → /intern) */
export function einladungsLink(path: string, origin: string): string {
  return `${origin.replace(/\/+$/, "")}${betriebZuIntern(path.startsWith("/") ? path : `/${path}`)}`;
}

/** Stand des zweiten Faktors in Worten für die Teamliste */
export function faktorText(s: Pick<StaffRow, "factor_verified" | "mfa_required">): { text: string; tone: "success" | "warning" | "neutral" } {
  if (s.factor_verified) return { text: "eingerichtet", tone: "success" };
  return s.mfa_required ? { text: "fehlt noch (Pflicht)", tone: "warning" } : { text: "nicht eingerichtet", tone: "neutral" };
}
